/**
 * RestaurantCard - Clean Redesign 2026
 * 
 * Changes from v2:
 * - REMOVED delivery mode badges (redundant with header DeliveryModeToggle)  
 * - REMOVED highlight strip ("Beliebt für...") — generic fake data
 * - REMOVED delivery border-t section from grid card
 * - Kept secondary delivery option as subtle inline text
 * - Cleaner, less cluttered cards with focus on name, cuisine, rating, distance
 */

import React, { memo, useState, useMemo, useCallback } from 'react';
import { Link } from 'wouter';
import { 
  Heart, MapPin, Clock, Star, Percent, Euro,
  UtensilsCrossed
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { slugify } from '@shared/utils';
import { isRestaurantOpenNow } from '@/utils/restaurantTimes';
import { useFilterStore } from '@/lib/FilterStore';
import { useRestaurantFavorite } from '@/hooks/use-favorites';
import type { RestaurantCardProps } from '@/types/search';

// ============================================
// DESIGN TOKENS
// ============================================

const DESIGN = {
  colors: {
    primary: '#009544',
    primaryLight: '#E8F5EE',
    primaryDark: '#007538',
  },
  radius: {
    card: '24px',
    image: '20px',
  },
  shadow: {
    md: '0 4px 16px rgba(0,0,0,0.06), 0 2px 4px rgba(0,0,0,0.04)',
  },
};

// ============================================
// HELPERS
// ============================================

function getImageUrl(path: string | undefined): string {
  if (!path) return '';
  if (path.startsWith('/')) return path;
  return `/images/${path}`;
}

function normalizeTagLabel(name: string): string {
  const mappings: Record<string, string> = {
    'Schnell Restaurant': 'Fastfood',
    'Asiatische Spezialitäten': 'Asiatisch',
    'Café & Bistro': 'Café',
    'Fisch & Sushi': 'Sushi',
    'Frühstücken': 'Frühstück',
  };
  return mappings[name] || name;
}

function formatPrice(price: number): string {
  return price.toFixed(2).replace('.', ',') + ' €';
}

function StarRating({ rating, size = 'sm' }: { rating: number; size?: 'sm' | 'md' }) {
  const starSize = size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4';
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  
  return (
    <div className="flex items-center gap-0.5">
      {[...Array(5)].map((_, i) => (
        <Star 
          key={i}
          className={cn(
            starSize,
            'transition-colors',
            i < fullStars 
              ? 'fill-amber-400 text-amber-400' 
              : i === fullStars && hasHalfStar
                ? 'fill-amber-400/50 text-amber-400'
                : 'fill-gray-200 text-gray-200'
          )} 
        />
      ))}
    </div>
  );
}

// ============================================
// PLACEHOLDER
// ============================================

const PremiumPlaceholder = memo(function PremiumPlaceholder({ variant = 'default' }: { variant?: 'default' | 'compact' }) {
  const iconSize = variant === 'compact' ? 'w-10 h-10' : 'w-16 h-16';
  return (
    <div className="absolute inset-0 bg-gradient-to-br from-[#E8F5EE] via-[#F0F7F4] to-[#D0E8DD] flex items-center justify-center">
      <div className="relative">
        <div className="absolute inset-0 bg-[#009544]/5 rounded-full blur-2xl scale-150" />
        <UtensilsCrossed className={cn(iconSize, "text-[#009544]/25 relative")} />
      </div>
    </div>
  );
});

// ============================================
// PREMIUM HERO CARD (Featured Display)
// ============================================

const PremiumHeroCard = memo(function PremiumHeroCard({
  restaurant,
  openingHours,
  deliveryHours,
  images,
  cuisines,
  distance,
  className,
}: RestaurantCardProps) {
  const [imageError, setImageError] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const { isFavorite, toggle: toggleFavorite } = useRestaurantFavorite(restaurant.id);
  const deliveryMode = useFilterStore((s) => s.deliveryMode);
  
  const effectiveImages = images || restaurant.images || [];
  const effectiveOpeningHours = openingHours || restaurant.openingHours || [];
  const effectiveCuisines = cuisines || restaurant.categories || [];
  const hasDelivery = (deliveryHours && deliveryHours.length > 0) || restaurant.delivery || (restaurant as any).has_delivery;
  const hasPickup = (restaurant as any).pickup || (restaurant as any).has_pickup;
  
  const rating = (restaurant as any).rating || (restaurant as any).averageRating;
  const reviewCount = (restaurant as any).reviewCount || (restaurant as any).review_count;
  const minOrderValue = (restaurant as any).minOrderValue || (restaurant as any).min_order_value;
  const deliveryTime = (restaurant as any).deliveryTime || (restaurant as any).delivery_time;
  const promotion = (restaurant as any).promotion || (restaurant as any).currentPromotion;
  const priceLevel = (restaurant as any).priceLevel || (restaurant as any).price_level;
  
  const heroImage = useMemo(() => {
    const nonMenuImages = effectiveImages.filter(img => 
      !(img.is_menu === true || img.isMenu === true)
    ).sort((a, b) => (a.sort_order || a.sortOrder || 0) - (b.sort_order || b.sortOrder || 0));
    return nonMenuImages[0];
  }, [effectiveImages]);
  
  const { isOpen } = isRestaurantOpenNow(effectiveOpeningHours);
  
  const cuisineText = useMemo(() => {
    return effectiveCuisines
      .slice(0, 3)
      .map(c => normalizeTagLabel(c.name))
      .join(' · ');
  }, [effectiveCuisines]);
  
  const displayDistance = distance ?? (restaurant as any).distanceKm ?? restaurant.distance;
  const hasDistance = typeof displayDistance === 'number' && !isNaN(displayDistance);
  const cardUrl = `/restaurants/${restaurant.slug || slugify(restaurant.name)}`;
  
  const handleFavoriteClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleFavorite();
  }, [toggleFavorite]);

  // Show the "other" delivery option as subtle hint
  const secondaryDeliveryHint = useMemo(() => {
    if (deliveryMode === 'delivery' && hasPickup) return 'Auch Abholung';
    if (deliveryMode === 'pickup' && hasDelivery) return 'Auch Lieferung';
    return null;
  }, [deliveryMode, hasDelivery, hasPickup]);

  return (
    <Link href={cardUrl}>
      <article
        className={cn(
          "group relative flex flex-col",
          "bg-white overflow-hidden",
          "transition-all duration-300 ease-out",
          "cursor-pointer touch-manipulation",
          "lg:hover:shadow-[0_20px_50px_rgba(0,0,0,0.12)]",
          "lg:hover:-translate-y-1",
          "active:scale-[0.995]",
          className
        )}
        style={{
          borderRadius: DESIGN.radius.card,
          boxShadow: DESIGN.shadow.md,
          border: '1px solid rgba(0,0,0,0.04)',
        }}
      >
        {/* Hero Image - 16:10 */}
        <div 
          className="relative w-full overflow-hidden"
          style={{ 
            aspectRatio: '16/10',
            borderRadius: `${DESIGN.radius.image} ${DESIGN.radius.image} 0 0`,
          }}
        >
          {heroImage && !imageError ? (
            <>
              <img
                src={getImageUrl(heroImage.path)}
                alt={restaurant.name}
                className={cn(
                  "w-full h-full object-cover",
                  "transition-transform duration-500 ease-out",
                  "lg:group-hover:scale-105",
                  !isOpen && "grayscale-[60%] brightness-90",
                  !isImageLoaded && "opacity-0",
                  isImageLoaded && "opacity-100"
                )}
                loading="lazy"
                decoding="async"
                onLoad={() => setIsImageLoaded(true)}
                onError={() => setImageError(true)}
              />
              <div className="absolute inset-0 bg-black/0 lg:group-hover:bg-black/5 transition-colors duration-300 pointer-events-none" />
            </>
          ) : (
            <PremiumPlaceholder />
          )}
          
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          
          {/* Top: Status + Promotion + Favorite */}
          <div className="absolute top-4 left-4 right-4 flex items-start justify-between z-10">
            <div className="flex items-center gap-2 flex-wrap">
              {isOpen ? (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold text-white backdrop-blur-sm bg-emerald-500/95 shadow-sm">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
                  </span>
                  Geöffnet
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold bg-gray-900/80 text-white/90 backdrop-blur-sm">
                  <Clock className="w-3 h-3" />
                  Geschlossen
                </span>
              )}
              
              {promotion && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11px] font-bold bg-amber-500/95 text-white backdrop-blur-sm">
                  <Percent className="w-3 h-3" />
                  {promotion}
                </span>
              )}
            </div>
            
            <button
              onClick={handleFavoriteClick}
              className="w-10 h-10 rounded-full flex items-center justify-center bg-white/95 backdrop-blur-sm hover:scale-110 active:scale-95 transition-transform shadow-md"
              aria-label={isFavorite ? "Aus Favoriten entfernen" : "Zu Favoriten hinzufügen"}
            >
              <Heart className={cn("w-4.5 h-4.5", isFavorite ? "fill-rose-500 text-rose-500" : "text-gray-600")} />
            </button>
          </div>
          
          {/* Bottom: Name overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-5 z-10">
            <h3 className="text-xl md:text-2xl font-bold text-white leading-tight line-clamp-2 tracking-tight drop-shadow-lg">
              {restaurant.name}
            </h3>
          </div>
        </div>
        
        {/* Info Section */}
        <div className="p-5 flex flex-col gap-2.5">
          {/* Row 1: Cuisine + Price + Rating */}
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              {cuisineText && (
                <span className="text-sm font-medium text-gray-700 truncate">{cuisineText}</span>
              )}
              {priceLevel && (
                <>
                  <span className="text-gray-300">·</span>
                  <span className="text-sm text-gray-400">{'€'.repeat(priceLevel)}</span>
                </>
              )}
            </div>
            {rating && (
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <StarRating rating={rating} size="sm" />
                <span className="text-sm font-semibold text-gray-800">{rating.toFixed(1)}</span>
                {reviewCount && <span className="text-xs text-gray-400">({reviewCount})</span>}
              </div>
            )}
          </div>
          
          {/* Row 2: Distance · Time · Min Order · Secondary delivery hint */}
          <div className="flex items-center gap-2.5 flex-wrap text-sm text-gray-500">
            {hasDistance && (
              <span className="inline-flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5 text-gray-400" />
                {displayDistance.toFixed(1)} km
              </span>
            )}
            {deliveryTime && (
              <>
                <span className="w-1 h-1 rounded-full bg-gray-300" />
                <span className="inline-flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5 text-gray-400" />
                  {deliveryTime} Min.
                </span>
              </>
            )}
            {minOrderValue && minOrderValue > 0 && (
              <>
                <span className="w-1 h-1 rounded-full bg-gray-300" />
                <span className="inline-flex items-center gap-1">
                  <Euro className="w-3.5 h-3.5 text-gray-400" />
                  ab {formatPrice(minOrderValue)}
                </span>
              </>
            )}
            {secondaryDeliveryHint && (
              <>
                <span className="w-1 h-1 rounded-full bg-gray-300" />
                <span className="text-xs text-gray-400">{secondaryDeliveryHint}</span>
              </>
            )}
          </div>
        </div>
      </article>
    </Link>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.restaurant.id === nextProps.restaurant.id &&
    prevProps.distance === nextProps.distance &&
    prevProps.variant === nextProps.variant
  );
});

// ============================================
// COMPACT GRID CARD (2-column layout)
// ============================================

const CompactGridCard = memo(function CompactGridCard({
  restaurant,
  openingHours,
  deliveryHours,
  images,
  cuisines,
  distance,
  className,
}: RestaurantCardProps) {
  const [imageError, setImageError] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const { isFavorite, toggle: toggleFavorite } = useRestaurantFavorite(restaurant.id);
  const deliveryMode = useFilterStore((s) => s.deliveryMode);
  
  const effectiveImages = images || restaurant.images || [];
  const effectiveOpeningHours = openingHours || restaurant.openingHours || [];
  const effectiveCuisines = cuisines || restaurant.categories || [];
  const hasDelivery = (deliveryHours && deliveryHours.length > 0) || restaurant.delivery || (restaurant as any).has_delivery;
  const hasPickup = (restaurant as any).pickup || (restaurant as any).has_pickup;
  
  const rating = (restaurant as any).rating || (restaurant as any).averageRating;
  const reviewCount = (restaurant as any).reviewCount || (restaurant as any).review_count;
  const deliveryTime = (restaurant as any).deliveryTime || (restaurant as any).delivery_time;
  const minOrderValue = (restaurant as any).minOrderValue || (restaurant as any).min_order_value;
  const priceLevel = (restaurant as any).priceLevel || (restaurant as any).price_level;
  
  const heroImage = useMemo(() => {
    const nonMenuImages = effectiveImages.filter(img => 
      !(img.is_menu === true || img.isMenu === true)
    ).sort((a, b) => (a.sort_order || a.sortOrder || 0) - (b.sort_order || b.sortOrder || 0));
    return nonMenuImages[0];
  }, [effectiveImages]);
  
  const { isOpen } = isRestaurantOpenNow(effectiveOpeningHours);
  
  const cuisineText = useMemo(() => {
    if (!effectiveCuisines.length) return null;
    const first = normalizeTagLabel(effectiveCuisines[0].name);
    if (effectiveCuisines.length > 1) {
      return `${first} · ${normalizeTagLabel(effectiveCuisines[1].name)}`;
    }
    return first;
  }, [effectiveCuisines]);
  
  const displayDistance = distance ?? (restaurant as any).distanceKm ?? restaurant.distance;
  const hasDistance = typeof displayDistance === 'number' && !isNaN(displayDistance);
  const cardUrl = `/restaurants/${restaurant.slug || slugify(restaurant.name)}`;
  
  const handleFavoriteClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleFavorite();
  }, [toggleFavorite]);

  const secondaryHint = useMemo(() => {
    if (deliveryMode === 'delivery' && hasPickup) return '+Abholung';
    if (deliveryMode === 'pickup' && hasDelivery) return '+Lieferung';
    return null;
  }, [deliveryMode, hasDelivery, hasPickup]);

  return (
    <Link href={cardUrl}>
      <article
        className={cn(
          "group relative flex flex-col h-full",
          "bg-white overflow-hidden",
          "transition-all duration-300 ease-out",
          "cursor-pointer touch-manipulation",
          "hover:shadow-[0_12px_40px_-12px_rgba(0,0,0,0.15)] hover:-translate-y-1",
          "border border-gray-100 hover:border-gray-200",
          className
        )}
        style={{
          borderRadius: '16px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.02)',
        }}
      >
        {/* Image - 4:3 */}
        <div className="relative w-full overflow-hidden" style={{ aspectRatio: '4/3' }}>
          {heroImage && !imageError ? (
            <>
              <img
                src={getImageUrl(heroImage.path)}
                alt=""
                className={cn(
                  "w-full h-full object-cover",
                  "transition-transform duration-500 ease-out",
                  "lg:group-hover:scale-[1.04]",
                  !isOpen && "grayscale-[50%] brightness-90",
                  !isImageLoaded && "opacity-0",
                  isImageLoaded && "opacity-100"
                )}
                loading="lazy"
                decoding="async"
                onLoad={() => setIsImageLoaded(true)}
                onError={() => setImageError(true)}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent lg:group-hover:from-black/45 transition-colors duration-300" />
            </>
          ) : (
            <PremiumPlaceholder variant="compact" />
          )}
          
          {/* Top: Status + Favorite */}
          <div className="absolute top-2.5 left-2.5 right-2.5 flex items-start justify-between z-10">
            {isOpen ? (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold text-white bg-emerald-500/95 shadow-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                Offen
              </span>
            ) : (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-[10px] font-bold bg-gray-900/90 text-white">
                Geschlossen
              </span>
            )}
            
            <button
              onClick={handleFavoriteClick}
              className="w-8 h-8 rounded-full flex items-center justify-center bg-white/95 backdrop-blur-sm hover:scale-110 active:scale-95 transition-transform shadow-md"
              aria-label={isFavorite ? "Aus Favoriten entfernen" : "Zu Favoriten hinzufügen"}
            >
              <Heart className={cn("w-4 h-4", isFavorite ? "fill-rose-500 text-rose-500" : "text-gray-500")} />
            </button>
          </div>
          
          {/* Bottom: Rating */}
          {rating && (
            <div className="absolute bottom-2.5 right-2.5 z-10">
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-bold bg-white/95 text-gray-900 backdrop-blur-sm shadow-sm">
                <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                {rating.toFixed(1)}
                {reviewCount && <span className="text-gray-400 font-medium text-[10px]">({reviewCount})</span>}
              </span>
            </div>
          )}
        </div>
        
        {/* Content */}
        <div className="p-3.5 flex flex-col gap-1.5 flex-1">
          <h3 className={cn(
            "text-[15px] font-bold leading-snug line-clamp-2 tracking-tight",
            isOpen ? "text-gray-900" : "text-gray-500"
          )}>
            {restaurant.name}
          </h3>
          
          {/* Cuisine + Price */}
          <div className="flex items-center gap-1.5 text-sm">
            {cuisineText && <span className="text-gray-600 truncate flex-1 min-w-0">{cuisineText}</span>}
            {priceLevel && <span className="text-gray-400 flex-shrink-0">{'€'.repeat(priceLevel)}</span>}
          </div>
          
          {/* Meta: Distance · Time · Min order */}
          <div className="flex items-center gap-2 text-xs text-gray-500 mt-auto pt-1">
            {hasDistance && (
              <span className="inline-flex items-center gap-1">
                <MapPin className="w-3 h-3 text-gray-400" />
                {displayDistance.toFixed(1)} km
              </span>
            )}
            {deliveryTime && (
              <>
                <span className="w-0.5 h-0.5 rounded-full bg-gray-300" />
                <span className="inline-flex items-center gap-1">
                  <Clock className="w-3 h-3 text-gray-400" />
                  {deliveryTime} min
                </span>
              </>
            )}
            {minOrderValue && minOrderValue > 0 && (
              <>
                <span className="w-0.5 h-0.5 rounded-full bg-gray-300" />
                <span>ab {formatPrice(minOrderValue)}</span>
              </>
            )}
            {secondaryHint && (
              <>
                <span className="w-0.5 h-0.5 rounded-full bg-gray-300" />
                <span className="text-gray-400">{secondaryHint}</span>
              </>
            )}
          </div>
        </div>
      </article>
    </Link>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.restaurant.id === nextProps.restaurant.id &&
    prevProps.distance === nextProps.distance &&
    prevProps.variant === nextProps.variant
  );
});

// ============================================
// LIST CARD - Compact horizontal
// ============================================

const MobileListCard = memo(function MobileListCard({
  restaurant,
  openingHours,
  deliveryHours,
  images,
  cuisines,
  distance,
  className,
}: RestaurantCardProps) {
  const [imageError, setImageError] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const { isFavorite, toggle: toggleFavorite } = useRestaurantFavorite(restaurant.id);
  const deliveryMode = useFilterStore((s) => s.deliveryMode);
  
  const effectiveImages = images || restaurant.images || [];
  const effectiveOpeningHours = openingHours || restaurant.openingHours || [];
  const effectiveCuisines = cuisines || restaurant.categories || [];
  const hasDelivery = (deliveryHours && deliveryHours.length > 0) || restaurant.delivery || (restaurant as any).has_delivery;
  const hasPickup = (restaurant as any).pickup || (restaurant as any).has_pickup;
  
  const rating = (restaurant as any).rating || (restaurant as any).averageRating;
  const reviewCount = (restaurant as any).reviewCount || (restaurant as any).review_count;
  const deliveryTime = (restaurant as any).deliveryTime || (restaurant as any).delivery_time;
  const minOrderValue = (restaurant as any).minOrderValue || (restaurant as any).min_order_value;
  const deliveryFee = (restaurant as any).deliveryFee || (restaurant as any).delivery_fee;
  const priceLevel = (restaurant as any).priceLevel || (restaurant as any).price_level;
  
  const heroImage = useMemo(() => {
    const nonMenuImages = effectiveImages.filter(img => 
      !(img.is_menu === true || img.isMenu === true)
    ).sort((a, b) => (a.sort_order || a.sortOrder || 0) - (b.sort_order || b.sortOrder || 0));
    return nonMenuImages[0];
  }, [effectiveImages]);
  
  const { isOpen } = isRestaurantOpenNow(effectiveOpeningHours);
  
  const cuisineText = useMemo(() => {
    if (!effectiveCuisines.length) return null;
    const first = normalizeTagLabel(effectiveCuisines[0].name);
    if (effectiveCuisines.length > 1) {
      return `${first} · ${normalizeTagLabel(effectiveCuisines[1].name)}`;
    }
    return first;
  }, [effectiveCuisines]);
  
  const displayDistance = distance ?? (restaurant as any).distanceKm ?? restaurant.distance;
  const hasDistance = typeof displayDistance === 'number' && !isNaN(displayDistance);
  const cardUrl = `/restaurants/${restaurant.slug || slugify(restaurant.name)}`;
  
  const handleFavoriteClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleFavorite();
  }, [toggleFavorite]);

  const secondaryHint = useMemo(() => {
    if (deliveryMode === 'delivery' && hasPickup) return 'Auch Abholung';
    if (deliveryMode === 'pickup' && hasDelivery) return 'Auch Lieferung';
    return null;
  }, [deliveryMode, hasDelivery, hasPickup]);

  // Initials for placeholder image
  const initials = restaurant.name
    .split(/\s+/)
    .map(w => w[0])
    .filter(Boolean)
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <Link href={cardUrl}>
      <article
        className={cn(
          "group relative flex items-stretch min-h-[7rem]",
          "bg-white overflow-hidden",
          "border border-gray-100 rounded-2xl",
          "transition-all duration-200 ease-out",
          "hover:shadow-[0_8px_30px_-12px_rgba(0,0,0,0.12)] hover:border-gray-200",
          "cursor-pointer touch-manipulation",
          !isOpen && "opacity-80",
          className
        )}
      >
        {/* Image Thumbnail */}
        <div className="relative flex-shrink-0 w-[130px] sm:w-[150px] self-stretch overflow-hidden bg-gray-100">
          {heroImage && !imageError ? (
            <img
              src={getImageUrl(heroImage.path)}
              alt=""
              className={cn(
                "w-full h-full object-cover transition-transform duration-500",
                "group-hover:scale-[1.06]",
                !isOpen && "grayscale-[40%] brightness-95",
                !isImageLoaded && "opacity-0",
                isImageLoaded && "opacity-100"
              )}
              loading="lazy"
              decoding="async"
              onLoad={() => setIsImageLoaded(true)}
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-[#E8F5EE] via-[#F0F7F4] to-[#D0E8DD] flex flex-col items-center justify-center gap-1.5">
              <UtensilsCrossed className="w-9 h-9 text-[#009544]/25" />
              <span className="text-xs font-bold text-[#009544]/30 tracking-wide">{initials}</span>
            </div>
          )}
          
          {/* Status Badge - top left */}
          <div className="absolute top-2 left-2">
            {isOpen ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold text-white bg-emerald-500/95 shadow-sm backdrop-blur-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                Offen
              </span>
            ) : (
              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold text-white/90 bg-gray-800/80 backdrop-blur-sm">
                Geschlossen
              </span>
            )}
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0 flex flex-col justify-center gap-1.5 p-3.5 sm:p-4">
          {/* Top: Name + Favorite */}
          <div className="flex items-start gap-2">
            <div className="flex-1 min-w-0">
              <h3 className={cn(
                "text-[15px] sm:text-base font-bold leading-snug line-clamp-1 tracking-tight",
                isOpen ? "text-gray-900" : "text-gray-500"
              )}>
                {restaurant.name}
              </h3>
              
              {/* cuisineText + price level */}
              <div className="flex items-center gap-1.5 mt-0.5">
                {cuisineText && (
                  <p className="text-[13px] text-gray-500 truncate">{cuisineText}</p>
                )}
                {priceLevel && (
                  <>
                    {cuisineText && <span className="text-gray-300">·</span>}
                    <span className="text-[13px] text-gray-400 flex-shrink-0">{'€'.repeat(Math.min(priceLevel, 4))}</span>
                  </>
                )}
              </div>
            </div>
            
            {/* Favorite button */}
            <button
              onClick={handleFavoriteClick}
              className={cn(
                "w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0",
                "transition-all duration-200 active:scale-90",
                isFavorite 
                  ? "bg-rose-50 text-rose-500" 
                  : "bg-gray-50 text-gray-300 hover:bg-gray-100 hover:text-gray-400"
              )}
              aria-label={isFavorite ? "Aus Favoriten entfernen" : "Zu Favoriten hinzufügen"}
            >
              <Heart className={cn("w-4 h-4", isFavorite && "fill-current")} />
            </button>
          </div>
          
          {/* Middle: Rating */}
          <div className="flex items-center gap-2">
            {rating ? (
              <span className="inline-flex items-center gap-1">
                <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                <span className="text-sm font-bold text-gray-800">{rating.toFixed(1)}</span>
                {reviewCount && <span className="text-xs text-gray-400">({reviewCount})</span>}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#009544]/8 text-[11px] font-semibold text-[#009544]">
                Neu
              </span>
            )}
          </div>
          
          {/* Bottom: Meta info */}
          <div className="flex items-center gap-2 text-xs text-gray-500">
            {hasDistance && (
              <span className="inline-flex items-center gap-0.5">
                <MapPin className="w-3 h-3 text-gray-400" />
                {displayDistance.toFixed(1)} km
              </span>
            )}
            {deliveryTime && (
              <>
                <span className="w-0.5 h-0.5 rounded-full bg-gray-300" />
                <span className="inline-flex items-center gap-0.5">
                  <Clock className="w-3 h-3 text-gray-400" />
                  {deliveryTime} min
                </span>
              </>
            )}
            {deliveryMode === 'delivery' && deliveryFee !== undefined && deliveryFee !== null && (
              <>
                <span className="w-0.5 h-0.5 rounded-full bg-gray-300" />
                <span className="font-medium">{deliveryFee === 0 ? 'Gratis' : `${formatPrice(deliveryFee)}`}</span>
              </>
            )}
            {deliveryMode === 'delivery' && minOrderValue && minOrderValue > 0 && (
              <>
                <span className="w-0.5 h-0.5 rounded-full bg-gray-300" />
                <span>ab {formatPrice(minOrderValue)}</span>
              </>
            )}
            {secondaryHint && (
              <>
                <span className="w-0.5 h-0.5 rounded-full bg-gray-300" />
                <span className="text-gray-400 text-[11px]">{secondaryHint}</span>
              </>
            )}
          </div>
        </div>
      </article>
    </Link>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.restaurant.id === nextProps.restaurant.id &&
    prevProps.distance === nextProps.distance &&
    prevProps.variant === nextProps.variant
  );
});

// ============================================
// EXPORT
// ============================================

export const RestaurantCard = memo(function RestaurantCard(props: RestaurantCardProps) {
  if (props.variant === 'list') return <MobileListCard {...props} />;
  if (props.variant === 'grid') return <CompactGridCard {...props} />;
  return <PremiumHeroCard {...props} />;
}, (prevProps, nextProps) => {
  return (
    prevProps.restaurant.id === nextProps.restaurant.id &&
    prevProps.distance === nextProps.distance &&
    prevProps.variant === nextProps.variant
  );
});

export default RestaurantCard;
